import { DealerDashboardSubpage } from '../../../resources/base/models/enums';

export type OfferStatusIdType =
  | '100607'
  | '100044'
  | '23'
  | '21'
  | '100045'
  | '22'
  | '100180'
  | '401185'
  | '100175'
  | '401186'
  | '100176'
  | '565'
  | '401632'
  | '100215'
  | '100217'
  | '400749'
  | '566'
  | '2664'
  | '567'
  | '100246'
  | '100051';

export type SubpageLabelType =
  | 'Proposition'
  | 'Complétude'
  | 'Annulé'
  | 'Expiré'
  | 'Accepté sous réserve'
  | 'Etude'
  | 'Refus'
  | 'Accepté définitif'
  | 'Signature en cours'
  | 'Bon à livrer'
  | 'Paiement demandé'
  | 'Payé'
  | 'Messages lus'
  | 'Messages non lus';

export const offerStatusIdOptionsList: Record<OfferStatusIdType, { label: string; subpageLabel: SubpageLabelType }> = {
  '100607': { label: 'Proposition initiale', subpageLabel: 'Proposition' },
  '100044': { label: 'Proposition', subpageLabel: 'Proposition' },
  '23': { label: 'Contre-proposition', subpageLabel: 'Complétude' },
  '21': { label: 'Complétude', subpageLabel: 'Complétude' },
  '100045': { label: 'Annulé', subpageLabel: 'Annulé' },
  '22': { label: 'Annulé', subpageLabel: 'Annulé' },
  '100180': { label: 'Accepté sous réserve', subpageLabel: 'Accepté sous réserve' },
  '401186': { label: 'Accepté sous réserve', subpageLabel: 'Accepté sous réserve' },
  '401185': { label: 'Étude', subpageLabel: 'Etude' },
  '100175': { label: 'Refusé', subpageLabel: 'Refus' },
  '100176': { label: 'Accepté définitif', subpageLabel: 'Accepté définitif' },
  '565': { label: 'Accepté définitif', subpageLabel: 'Accepté définitif' },
  '401632': { label: 'Signature en attente', subpageLabel: 'Signature en cours' },
  '100215': { label: 'Bon à livrer', subpageLabel: 'Bon à livrer' },
  '100217': { label: 'Bon à livrer', subpageLabel: 'Bon à livrer' },
  '400749': { label: 'Paiement demandé', subpageLabel: 'Paiement demandé' },
  '566': { label: 'Payé', subpageLabel: 'Payé' },
  '2664': { label: 'Payé', subpageLabel: 'Payé' },
  '567': { label: 'Payé', subpageLabel: 'Payé' },
  '100246': { label: 'Payé', subpageLabel: 'Payé' },
  '100051': { label: 'Expiré', subpageLabel: 'Expiré' },
};

export const getStatusIdsBySubpage = (subpage: SubpageLabelType): OfferStatusIdType[] =>
  Object.entries(offerStatusIdOptionsList)
    .filter(([_, value]) => value.subpageLabel.includes(subpage))
    .map(([key]) => key as OfferStatusIdType);

export const getStatusIdsByLabel = (label: string): OfferStatusIdType[] =>
  Object.entries(offerStatusIdOptionsList)
    .filter(([_, value]) => value.label === label)
    .map(([key]) => key as OfferStatusIdType);

export const filteringStatusIdsStudiesSubpage: OfferStatusIdType[] = [
  '100180',
  '401186',
  '401185',
  // "Expired" is disabled for this release because it is not yet supported by the backoffice.
  // '100051',
  '100175',
  '22',
  '100045',
];

export const filteringStatusIdsAcceptedSubpage: OfferStatusIdType[] = [
  '100176',
  '565',
  '401632',
  '100215',
  '100217',
  '400749',
];

export const filterStatusIdsQuotesSubpage: OfferStatusIdType[] = [
  '100044',
  '100607',
  '23',
  '21',
  // "Expired" is disabled for this release because it is not yet supported by the backoffice.
  // '100051',
  '22',
  '100045',
];

export const getQuoteStatusIdsGroupedByLabel = (
  searchFieldStatusDropdownIds: OfferStatusIdType[]
): Array<{ value: OfferStatusIdType[]; label: string }> => {
  const groupedByLabel: Record<string, OfferStatusIdType[]> = {};

  Object.entries(offerStatusIdOptionsList)
    .filter(([key]) => searchFieldStatusDropdownIds.includes(key as OfferStatusIdType))
    .forEach(([key, { label }]) => {
      if (!groupedByLabel[label]) {
        groupedByLabel[label] = [];
      }
      groupedByLabel[label].push(key as OfferStatusIdType);
    });

  const mapped = Object.entries(groupedByLabel).map(([label, keys]) => ({
    value: keys,
    label,
  }));

  const orderMap = new Map(searchFieldStatusDropdownIds.map((v, i) => [v, i]));

  return mapped.sort((a, b) => {
    const aIndex = orderMap.get(a.value.find((v) => orderMap.has(v))!) ?? Infinity;
    const bIndex = orderMap.get(b.value.find((v) => orderMap.has(v))!) ?? Infinity;

    return aIndex - bIndex;
  });
};

export const groupedStatusIdsBySubpageMap = {
  [DealerDashboardSubpage.Accepted]: getQuoteStatusIdsGroupedByLabel(filteringStatusIdsAcceptedSubpage),
  [DealerDashboardSubpage.Summary]: getQuoteStatusIdsGroupedByLabel(filterStatusIdsQuotesSubpage),
  [DealerDashboardSubpage.Studies]: getQuoteStatusIdsGroupedByLabel(filteringStatusIdsStudiesSubpage),
  [DealerDashboardSubpage.Run]: [],
  [DealerDashboardSubpage.Conversations]: [],
};

// Default selection for the Proposition (Summary) status filter: excludes the cancelled
// statuses and 'Expiré' (100051) so expired offers are selectable but not fetched by default.
export const removedAnnuleInitialQuotesSubpage: OfferStatusIdType[] = filterStatusIdsQuotesSubpage.filter(
  (id) => !['22', '100045', '100051'].includes(id)
);

// Default selection for the Études (Studies) status filter: excludes 'Expiré' (100051) or Annulé (22, 100045) so
// expired and cancelled offers are selectable but not fetched by default.
export const defaultSelectedStudiesSubpage: OfferStatusIdType[] = filteringStatusIdsStudiesSubpage.filter(
  (id) => !['100051', '100045', '22'].includes(id)
);

// Per-subpage default-selected statuses for the status filter dropdown (i.e. excluding
// 'Expiré'). Subpages absent from this map default to all of their grouped statuses.
export const defaultSelectedStatusIdsBySubpageMap: Partial<Record<DealerDashboardSubpage, OfferStatusIdType[]>> = {
  [DealerDashboardSubpage.Summary]: removedAnnuleInitialQuotesSubpage,
  [DealerDashboardSubpage.Studies]: defaultSelectedStudiesSubpage,
};

// Denylist gate for action buttons: returns true when any selected offer's status is in the
// button's `disabledOnStatuses` list (e.g. an expired offer disables Annuler/Transfert/Comparer).
export const isButtonDisabledByStatus = (
  disabledOnStatuses: OfferStatusIdType[] | undefined,
  selectedStatusIds: Array<OfferStatusIdType | undefined>
): boolean => {
  if (!disabledOnStatuses?.length || !selectedStatusIds.length) {
    return false;
  }
  return selectedStatusIds.some((statusId) => !!statusId && disabledOnStatuses.includes(statusId));
};

export const eligibleForQuoteComparison: OfferStatusIdType[] = ['100607', '100044', '23'];
